import * as analytics from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {pluralize} from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher from 'lib/Dispatcher';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceDAO from 'molbio/aa-sequences/AaSequenceDAO';
import {Protein} from 'molbio/aa-sequences/util/AaSequenceConstants';
import handleAaSequenceUpdateError from 'molbio/aa-sequences/util/handleAaSequenceUpdateError';
import FeatureLibraryModal from 'molbio/feature-libraries/FeatureLibraryModal';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {closeAnnotationForm} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import {FeatureDisplayData} from 'toolbar/feature-panel/FeatureRowItem';
import legacyRunModal from 'ui-library/legacyRunModal';
import sortedBy from 'util/sortedBy';

// Keep in sync with value in benchling/molbio/aa_sequences/models/protein_annotation_version
export const MAX_AA_SEQUENCE_ANNOTATION_NAME_LENGTH = 255;
// Keep in sync with value in benchling/molbio/aa_sequences/models/size_limits
export const MAX_AA_SEQUENCE_ANNOTATION_NOTES_LENGTH = 10000;

export interface DisplayAaSequenceAnnotation extends FeatureDisplayData {
  id: string;
  displayStart: number;
  displayEnd: number | null;
}

export function getSortedDisplayAnnotations(
  protein: Protein | undefined,
  canEditFeatures: boolean,
  selectedAnnotationId: string | null,
  hoveredAnnotationId: string | null
): Array<DisplayAaSequenceAnnotation> {
  const proteinLength = protein?.version.aminoAcids.length ?? 0;
  const rowData =
    protein == null
      ? []
      : protein.version.annotations.map(({id, name, start, end, color, isFromPart}) => ({
          id,
          cid: id,
          name,
          displayStart: sequtil.modelToDisplay(start, 1),
          displayEnd: sequtil.modelEndToDisplay(end, proteinLength, 1),
          color,
          editable: canEditFeatures && !isFromPart,
          selected: selectedAnnotationId === id,
          hover: hoveredAnnotationId === id,
        }));
  return sortedBy(rowData, ['displayStart', 'displayEnd']);
}

export async function handleAutoAnnotateAaSequenceModal(
  proteinId: string,
  originalNumAnnotations: number,
  latestVersionId: number
): Promise<void> {
  const featureLibraryIds = await legacyRunModal(FeatureLibraryModal, {});
  if (featureLibraryIds == null) {
    return;
  }
  analytics.track('protein', 'add_auto_annotations_click', {
    product: ANALYTICS_MODULE.MOLBIO,
    protein_id: proteinId,
    featureLibraryIds,
  });
  AaSequenceDAO.autoAnnotate(proteinId, latestVersionId, featureLibraryIds)
    .done((aaSequenceResponse: Protein) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.AA_SEQUENCE_UPDATE_COMPLETED,
        proteinId,
        newAaSequence: aaSequenceResponse,
      });
      AaSequenceActions.fetchVersions(proteinId);
      const numAdded = aaSequenceResponse.version.annotations.length - originalNumAnnotations;

      analytics.track('protein', 'completed_auto_annotate', {
        product: ANALYTICS_MODULE.MOLBIO,
        annotationCount: numAdded,
        protein_id: proteinId,
      });
      if (numAdded === 0) {
        notifications.info('No new features were found.');
      } else {
        notifications.info(`Annotated ${numAdded} ${pluralize(numAdded, 'feature')}.`);
      }
    })
    .fail((res) => {
      analytics.track('protein', 'auto_annotate_fail', {
        product: ANALYTICS_MODULE.MOLBIO,
        error: notifications.getFriendlyError(res),
        protein_id: proteinId,
      });
      handleAaSequenceUpdateError(res);
    });
}

interface AnnotationInput {
  name: string;
  type: string;
  notes: string;
  start: number | null;
  end: number | null;
  color: string;
}

/**
 * Validates the given annotation and returns an error message if it is invalid.
 */
function validateAnnotationInput(proteinLength: number, annotation: AnnotationInput): string | null {
  if (!annotation.name) {
    return 'An annotation must have a valid name';
  } else if (annotation.name.length > MAX_AA_SEQUENCE_ANNOTATION_NAME_LENGTH) {
    return `Annotation name must be less than ${MAX_AA_SEQUENCE_ANNOTATION_NAME_LENGTH} characters`;
  }
  if (annotation.notes != null && annotation.notes.length > MAX_AA_SEQUENCE_ANNOTATION_NOTES_LENGTH) {
    return `Annotation notes must be less than ${MAX_AA_SEQUENCE_ANNOTATION_NOTES_LENGTH} characters`;
  }
  if (annotation.start == null || annotation.end == null) {
    return 'An annotation must have a valid start and end';
  }
  if (!sequtil.isValidRange(annotation.start, annotation.end, proteinLength, false)) {
    return `Start and end values must be between 1 and ${proteinLength}`;
  }
  return null;
}

/**
 * Validate an annotation and save changes by either creating a new annotation or editing the existing one.
 * If the annotation is not valid for the protein, shows an error toast.
 */
export function saveAnnotation(
  proteinId: string,
  latestVersionId: number,
  proteinLength: number,
  selectedAnnotationId: string | null,
  annotation: AnnotationInput
): void {
  // Validation and saving expects the location representing the end of the sequence to be 0, so adjust accordingly
  const adjustedAnnotationInput = {...annotation, end: annotation.end === proteinLength ? 0 : annotation.end};
  const maybeError = validateAnnotationInput(proteinLength, adjustedAnnotationInput);
  if (maybeError != null) {
    notifications.error(maybeError);
    return;
  }

  let deferred;
  if (selectedAnnotationId == null) {
    analytics.track('protein', 'created_annotation', {
      product: ANALYTICS_MODULE.MOLBIO,
      actionCategory: ACTION_CATEGORY.WRITE,
      protein_id: proteinId,
    });
    deferred = AaSequenceDAO.createAnnotation(
      proteinId,
      adjustedAnnotationInput.name,
      adjustedAnnotationInput.type,
      adjustedAnnotationInput.notes,
      adjustedAnnotationInput.start!,
      adjustedAnnotationInput.end!,
      adjustedAnnotationInput.color,
      latestVersionId
    );
  } else {
    deferred = AaSequenceDAO.updateAnnotation(proteinId, selectedAnnotationId, {
      ...adjustedAnnotationInput,
      // At this point, we have already validated that start and end exist in validateAnnotationInput
      start: adjustedAnnotationInput.start!,
      end: adjustedAnnotationInput.end!,
      expectedVersionId: latestVersionId,
    });
  }
  deferred
    .done((aaSequenceResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.AA_SEQUENCE_UPDATE_COMPLETED,
        proteinId,
        newAaSequence: aaSequenceResponse,
      });
      notifications.success('Annotations saved successfully');
      closeAnnotationForm(proteinId);
      AaSequenceActions.fetchVersions(proteinId);
    })
    .fail(handleAaSequenceUpdateError);
}

/**
 * Delete the given annotation from the given AA sequence.
 */
export function deleteAnnotation(
  proteinId: string,
  latestVersionId: number,
  annotationId: string,
  selectedAnnotationId: string | null
): void {
  AaSequenceDAO.deleteAnnotation(proteinId, annotationId, latestVersionId)
    .done((aaSequenceResponse) => {
      notifications.success('Annotation deleted successfully');
      if (annotationId === selectedAnnotationId) {
        closeAnnotationForm(proteinId);
      }
      AppDispatcher.dispatch({
        actionType: EventConstants.AA_SEQUENCE_UPDATE_COMPLETED,
        proteinId,
        newAaSequence: aaSequenceResponse,
      });
      AaSequenceActions.fetchVersions(proteinId);
    })
    .fail(handleAaSequenceUpdateError);
}
